import { useState, useEffect, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, X, Shirt, Layers } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Page, Main, Card, LoadingState, ErrorState, EmptyState, Thumb, inputClass } from '@/components/ui';
import { useApi } from '@/hooks/useApi';
import { cn } from '@/lib/utils';
import type { Item } from '@/lib/types';

interface SearchFit {
  id: string;
  name: string;
  current_version: number;
}

interface SearchResponse {
  items: Item[];
  fits: SearchFit[];
}

export function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') ?? '';
  const [input, setInput] = useState(query);

  // Keep the input synced when the URL changes (e.g. back/forward navigation).
  useEffect(() => {
    setInput(query);
  }, [query]);

  const trimmed = query.trim();
  const { data, loading, error } = useApi<SearchResponse>(
    trimmed ? `/search?q=${encodeURIComponent(trimmed)}` : null,
  );

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next = input.trim();
    setSearchParams(next ? { q: next } : {});
  };

  const clear = () => {
    setInput('');
    setSearchParams({});
  };

  const items = data?.items ?? [];
  const fits = data?.fits ?? [];
  const hasResults = items.length > 0 || fits.length > 0;

  return (
    <Page>
      <PageHeader section="Search" />
      <Main className="space-y-5">
        <form onSubmit={submit}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Search items and fits…"
              autoFocus
              className={cn(inputClass, 'pl-10 pr-10')}
            />
            {input && (
              <button
                type="button"
                onClick={clear}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-secondary"
                aria-label="Clear search"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            )}
          </div>
        </form>

        {!trimmed && (
          <EmptyState
            icon={<Search className="w-8 h-8" />}
            title="Search your wardrobe"
            description="Find items and fits by name, brand, or category."
          />
        )}

        {trimmed && loading && <LoadingState />}
        {trimmed && error && <ErrorState message={error} />}

        {trimmed && !loading && !error && !hasResults && (
          <EmptyState
            icon={<Search className="w-8 h-8" />}
            title="No results"
            description={`Nothing matched “${trimmed}”. Try a different search.`}
          />
        )}

        {trimmed && !loading && !error && items.length > 0 && (
          <section className="space-y-3">
            <h2 className="text-sm font-semibold text-foreground">
              Items <span className="text-muted-foreground font-normal">({items.length})</span>
            </h2>
            <div className="space-y-2">
              {items.map((item) => (
                <Link key={item.id} to={`/wardrobe/item/${item.id}`}>
                  <Card className="p-3 flex items-center gap-3 hover:border-primary/30 transition-colors">
                    <Thumb url={item.primaryPhotoUrl} alt={item.name} className="w-12 h-12 rounded-lg flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{item.name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {[item.brand, item.category].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                    <Shirt className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  </Card>
                </Link>
              ))}
            </div>
          </section>
        )}

        {trimmed && !loading && !error && fits.length > 0 && (
          <section className="space-y-3">
            <h2 className="text-sm font-semibold text-foreground">
              Fits <span className="text-muted-foreground font-normal">({fits.length})</span>
            </h2>
            <div className="space-y-2">
              {fits.map((fit) => (
                <Link key={fit.id} to={`/fits/${fit.id}`}>
                  <Card className="p-3 flex items-center gap-3 hover:border-primary/30 transition-colors">
                    <div className="w-12 h-12 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
                      <Layers className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{fit.name}</p>
                      <p className="text-xs text-muted-foreground">v{fit.current_version}</p>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </section>
        )}
      </Main>
    </Page>
  );
}
